import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Download, FileText, Subtitles, Code, RotateCcw, Sparkles, ClipboardCopy, Check, FileDown, FileType2 } from 'lucide-react';
import jsPDF from 'jspdf';
import { buildDocxBlob, docxFilenameFromTitle } from '@/lib/wordExport';

interface Speaker {
  speaker: string;
  text: string;
  timestamp?: string;
}

interface ExportToolbarProps {
  onExport: (format: 'txt' | 'srt' | 'vtt' | 'json') => void;
  onStartNew: () => void;
  transcript: Speaker[];
  title?: string;
  summary?: string;
}

function buildMarkdown(transcript: Speaker[], title: string): string {
  const speakers = [...new Set(transcript.map((s) => s.speaker))];

  let md = `# ${title}\n\n`;
  md += `## Speakers\n`;
  for (const name of speakers) {
    md += `- ${name}\n`;
  }
  md += `\n## Transcript\n\n`;
  for (const seg of transcript) {
    const ts = seg.timestamp ? ` (${seg.timestamp})` : '';
    md += `**${seg.speaker}**${ts}\n${seg.text}\n\n`;
  }
  return md.trimEnd() + '\n';
}

export function ExportToolbar({ onExport, onStartNew, transcript, title, summary }: ExportToolbarProps) {
  const [copied, setCopied] = useState(false);

  function handlePdfExport() {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let yPos = 20;

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    const displayTitle = title || 'Transcript';
    const titleLines = doc.splitTextToSize(displayTitle, maxWidth);
    doc.text(titleLines, margin, yPos);
    yPos += titleLines.length * 8 + 10;

    // Summary section
    if (summary) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Summary', margin, yPos);
      yPos += 7;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const summaryLines = doc.splitTextToSize(summary, maxWidth);
      for (const line of summaryLines) {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, margin, yPos);
        yPos += 5;
      }
      yPos += 10;
    }

    // Transcript section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Transcript', margin, yPos);
    yPos += 10;

    doc.setFontSize(10);
    for (const seg of transcript) {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }

      // Speaker name and timestamp
      doc.setFont('helvetica', 'bold');
      const ts = seg.timestamp ? ` (${seg.timestamp})` : '';
      doc.text(`${seg.speaker}${ts}`, margin, yPos);
      yPos += 5;

      // Text
      doc.setFont('helvetica', 'normal');
      const textLines = doc.splitTextToSize(seg.text, maxWidth);
      for (const line of textLines) {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, margin, yPos);
        yPos += 5;
      }
      yPos += 5;
    }

    const filename = `${displayTitle.replace(/[^a-z0-9]/gi, '_').substring(0, 50)}.pdf`;
    doc.save(filename);
  }

  const exportButtons = [
    { format: 'txt' as const, label: 'TXT', icon: FileText },
    { format: 'srt' as const, label: 'SRT', icon: Subtitles },
    { format: 'vtt' as const, label: 'VTT', icon: Subtitles },
    { format: 'json' as const, label: 'JSON', icon: Code },
  ];

  const displayTitle = title || 'Transcript';

  function handleAiExport() {
    const md = buildMarkdown(transcript, displayTitle);
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${displayTitle.replace(/[^a-z0-9]/gi, '_').substring(0, 50)}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function handleDocxExport() {
    const blob = await buildDocxBlob({ title: displayTitle, segments: transcript, summary });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = docxFilenameFromTitle(displayTitle);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function handleCopy() {
    const md = buildMarkdown(transcript, displayTitle);
    navigator.clipboard.writeText(md).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
      className="w-full max-w-4xl mx-auto py-6"
    >
      <div className="flex items-center justify-between gap-4 p-6 bg-white border-2 border-gray-200 rounded-lg">
        <div className="flex items-center gap-3">
          <Download className="w-5 h-5 text-[#0066FF]" strokeWidth={2} />
          <span
            className="text-sm font-semibold uppercase tracking-wider"
            style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '0.1em' }}
          >
            Export Transcript
          </span>
        </div>

        <div className="flex items-center gap-3">
          <motion.div
            whileTap={{ scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
          >
            <Button
              onClick={handleCopy}
              variant="outline"
              className={`h-10 px-5 border-2 transition-colors duration-200 font-semibold ${
                copied
                  ? 'border-green-500 text-green-600'
                  : 'border-[#0066FF] text-[#0066FF] hover:bg-[#0066FF] hover:text-white'
              }`}
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" strokeWidth={2} />
                  Copied!
                </>
              ) : (
                <>
                  <ClipboardCopy className="w-4 h-4 mr-2" strokeWidth={2} />
                  Copy
                </>
              )}
            </Button>
          </motion.div>

          <motion.div
            whileTap={{ scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
          >
            <Button
              onClick={handleAiExport}
              variant="outline"
              className="h-10 px-5 border-2 border-[#0066FF] text-[#0066FF] hover:bg-[#0066FF] hover:text-white transition-colors duration-200 font-semibold"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              <Sparkles className="w-4 h-4 mr-2" strokeWidth={2} />
              AI Ready
            </Button>
          </motion.div>

          <motion.div
            whileTap={{ scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
          >
            <Button
              onClick={handlePdfExport}
              variant="outline"
              className="h-10 px-5 border-2 border-[#0066FF] text-[#0066FF] hover:bg-[#0066FF] hover:text-white transition-colors duration-200 font-semibold"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              <FileDown className="w-4 h-4 mr-2" strokeWidth={2} />
              PDF
            </Button>
          </motion.div>

          <motion.div
            whileTap={{ scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
          >
            <Button
              onClick={handleDocxExport}
              variant="outline"
              className="h-10 px-5 border-2 border-[#0066FF] text-[#0066FF] hover:bg-[#0066FF] hover:text-white transition-colors duration-200 font-semibold"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              <FileType2 className="w-4 h-4 mr-2" strokeWidth={2} />
              Word
            </Button>
          </motion.div>

          {exportButtons.map(({ format, label, icon: Icon }) => (
            <motion.div
              key={format}
              whileTap={{ scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 400, damping: 17 }}
            >
              <Button
                onClick={() => onExport(format)}
                variant="outline"
                className="h-10 px-5 border-2 border-[#0066FF] text-[#0066FF] hover:bg-[#0066FF] hover:text-white transition-colors duration-200 font-semibold"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <Icon className="w-4 h-4 mr-2" strokeWidth={2} />
                {label}
              </Button>
            </motion.div>
          ))}

          <div className="h-8 w-px bg-gray-300 mx-2" />

          <motion.div
            whileTap={{ scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
          >
            <Button
              onClick={onStartNew}
              variant="outline"
              className="h-10 px-5 border-2 border-gray-300 hover:border-[#0066FF] hover:text-[#0066FF] transition-colors duration-200 font-semibold"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              <RotateCcw className="w-4 h-4 mr-2" strokeWidth={2} />
              Start New
            </Button>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
